import { decodeOneOf, encodeOneOf, safeId } from '../../../../domain';
import { RestrictedKeys } from '../restricted.key';
import { Places } from '../../../route-consts';
import { SLEEP_PERIOD_CODES } from './sleep-report.periods';
import { SLEEP_LAYOUT_CODES, SLEEP_TABLE_CODES } from './sleep-report.tables';
/** Prefixo dos nomes deste arquivo na intencao - unico na aplicacao (ver `url-codes.test.ts`). */
const PREFIX = 'sr';
/** Os parametros deste place: vao para o endereco. */
const P = {
    CompanyId: `${PREFIX}a`,
    // A SELECAO de atletas nao viaja no endereco: ela e salva no servidor e aqui vem
    // so o id dela. Vinte e um ids numa URL seriam ilegiveis, e a selecao se repete
    // semana apos semana - guardada com nome, ela vira um lugar a que se volta.
    SleepSelection: `${PREFIX}b`,
    /** Qual periodo do relatorio. Ver `SLEEP_PERIOD_CODES`. */
    SleepPeriod: `${PREFIX}c`,
    /**
     * O intervalo escolhido a mao, quando o periodo e `livre`.
     *
     * Nomes proprios, e nao os `de`/`ate` dos Monitoramentos: um nome de parametro
     * tem um dono so, e aquele pertence a outra tela.
     */
    SleepFrom: `${PREFIX}d`,
    SleepTo: `${PREFIX}e`,
    /** Qual das seis tabelas esta aberta. Ver `SLEEP_TABLE_CODES`. */
    SleepTable: `${PREFIX}f`,
    /** Noite ou atleta na linha (B-36). Ver `SLEEP_LAYOUT_CODES`. */
    SleepLayout: `${PREFIX}g`,
    /**
     * UM colaborador, sem grupo salvo - quem chega pela celula de sono da lista
     * (pedido de 19/10/2026). Um id so cabe no endereco; o que nao cabe e a lista
     * de marcados, e essa continua fora.
     */
    SleepPatient: `${PREFIX}h`
};
/**
 * O que o relatorio de sono esta mostrando.
 *
 * Tres coisas, e nenhuma delas e a lista de atletas: a selecao mora no servidor e
 * aqui viaja so o id. Vinte e um ids no endereco seriam ilegiveis, e a selecao se
 * repete semana apos semana - com nome, ela vira um lugar a que se volta.
 */
export class SleepReportKeys extends RestrictedKeys {
    get place() {
        return Places.sleepReport;
    }
    /**
     * O endereco, em texto claro, do relatorio de UM colaborador num intervalo - para o
     * `href` da celula de sono na lista. Quem sela e o `MainPresenter.sealedAddress`; os
     * nomes e codigos so esta classe conhece.
     */
    static plainAddressOf(target) {
        const params = [
            [P.CompanyId, target.companyId],
            [P.SleepPatient, target.patientId],
            [P.SleepPeriod, encodeOneOf('custom', SLEEP_PERIOD_CODES)],
            [P.SleepFrom, target.from],
            [P.SleepTo, target.to]
        ];
        const query = params
            .filter(([, value]) => value !== undefined)
            .map(([name, value]) => `${name}=${value}`)
            .join('&');
        return `${Places.sleepReport.name}?${query}`;
    }
    /**
     * De qual empresa sao os atletas oferecidos.
     *
     * So o administrador escolhe - o admin de empresa e o profissional ja estao
     * presos a uma. Um paciente pertence a UMA empresa, entao um relatorio que
     * misturasse duas nao descreveria time nenhum.
     */
    get companyId() {
        return safeId(this._intent.getParameterAsNumber(P.CompanyId));
    }
    set companyId(value) {
        this._intent.setParameter(P.CompanyId, value);
    }
    /**
     * O relatorio de UM colaborador, sem grupo. Ausente e o de sempre: todos os
     * atletas da empresa. So vale sem `selectionId` - um grupo aberto manda.
     */
    get patientId() {
        return safeId(this._intent.getParameterAsNumber(P.SleepPatient));
    }
    set patientId(value) {
        this._intent.setParameter(P.SleepPatient, value);
    }
    /** Qual selecao salva esta aberta. Ausente e "nenhuma ainda". */
    get selectionId() {
        return safeId(this._intent.getParameterAsNumber(P.SleepSelection));
    }
    set selectionId(value) {
        this._intent.setParameter(P.SleepSelection, value);
    }
    /**
     * Se o ENDERECO diz um periodo. Quando nao diz, vale o ultimo periodo escolhido no
     * painel (B-35, `recallPeriod`), e nao o padrao fixo desta tela.
     */
    get periodInAddress() {
        return (this._intent.getParameterAsString(P.SleepPeriod) !== undefined ||
            this._intent.getParameterAsString(P.SleepFrom) !== undefined ||
            this._intent.getParameterAsString(P.SleepTo) !== undefined);
    }
    get period() {
        return decodeOneOf(this._intent.getParameterAsString(P.SleepPeriod), SLEEP_PERIOD_CODES, '7d');
    }
    set period(value) {
        // O padrao fica FORA do endereco: link limpo continua limpo.
        this._intent.setParameter(P.SleepPeriod, value === '7d' ? undefined : encodeOneOf(value, SLEEP_PERIOD_CODES));
    }
    /**
     * As pontas do intervalo, quando o periodo e `livre`. `yyyy-mm-dd`.
     *
     * Nao ha valor padrao: sem elas o periodo cai no atalho, e um endereco que
     * carregue data invalida e tratado como se nao a tivesse.
     */
    get from() {
        return isoDate(this._intent.getParameterAsString(P.SleepFrom));
    }
    set from(value) {
        this._intent.setParameter(P.SleepFrom, value ? value : undefined);
    }
    get to() {
        return isoDate(this._intent.getParameterAsString(P.SleepTo));
    }
    set to(value) {
        this._intent.setParameter(P.SleepTo, value ? value : undefined);
    }
    get table() {
        return decodeOneOf(this._intent.getParameterAsString(P.SleepTable), SLEEP_TABLE_CODES, 'deitou');
    }
    set table(value) {
        this._intent.setParameter(P.SleepTable, value === 'deitou' ? undefined : encodeOneOf(value, SLEEP_TABLE_CODES));
    }
    /**
     * A disposicao da tabela, quando o leitor a ESCOLHEU. Ausente, vale o padrao do
     * modo (B-44): sem grupo, um atleta por linha; com grupo, uma noite por linha.
     */
    get layout() {
        const raw = this._intent.getParameterAsString(P.SleepLayout);
        return raw === undefined ? undefined : decodeOneOf(raw, SLEEP_LAYOUT_CODES, 'noites');
    }
    set layout(value) {
        this._intent.setParameter(P.SleepLayout, value === undefined ? undefined : encodeOneOf(value, SLEEP_LAYOUT_CODES));
    }
}
/**
 * Uma data que chegou de fora, aceita so na forma `yyyy-mm-dd` e se existir.
 *
 * `2026-02-31` casa com a forma e nao e dia nenhum: o `Date` o empurraria para marco
 * calado, e o relatorio abriria num intervalo que ninguem pediu.
 */
function isoDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return undefined;
    }
    const when = new Date(`${value}T12:00:00`);
    return Number.isNaN(when.getTime()) || when.toISOString().slice(0, 10) !== value ? undefined : value;
}
//# sourceMappingURL=sleep-report.key.js.map